import React, { useState, useEffect } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast } from "../../../utils/dialog";

const BeautyGalleryManager = () => {
  const {
    beautyGalleryData,
    fetchBeautyGalleryData,
    addBeautyGallerySection,
    addImageToBeautySection,
    deleteImageFromBeautySection,
    deleteBeautyGallerySection,
    updateBeautyGallerySectionName,
    updateBeautyImageLabel,
  } = useData();

  console.log("Beauty Gallery Data:", beautyGalleryData);

  const [newSectionName, setNewSectionName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLabel, setImageLabel] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");

  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionName, setEditingSectionName] = useState("");

  const [editingImageId, setEditingImageId] = useState(null);
  const [newLabel, setNewLabel] = useState("");

  useEffect(() => {
    fetchBeautyGalleryData(); // fetch on mount
  }, []);

  const handleAddSection = async () => {
    if (newSectionName.trim() !== "") {
      await addBeautyGallerySection(newSectionName);
      setNewSectionName("");
    }
  };

  const handleAddImage = async () => {
    if (selectedSectionId && selectedFile && imageLabel.trim() !== "") {
      await addImageToBeautySection(selectedSectionId, selectedFile, imageLabel);
      setSelectedFile(null);
      setImageLabel("");
    }
  };

  const handleEditSectionName = async (sectionId) => {
    if (editingSectionName.trim() !== "") {
      await updateBeautyGallerySectionName(sectionId, editingSectionName);
      setEditingSectionId(null);
      setEditingSectionName("");
    }
  };

  const startEditingLabel = (imgId, currentLabel) => {
    setEditingImageId(imgId);
    setNewLabel(currentLabel);
  };

  const handleUpdateLabel = async (sectionId, imgId) => {
    if (newLabel.trim() !== "") {
      await updateBeautyImageLabel(sectionId, imgId, newLabel);
      setEditingImageId(null);
      setNewLabel("");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (
      await confirmDelete({
        title: "Delete Section?",
        text: "This section and all of its images will be permanently deleted.",
      })
    ) {
      await deleteBeautyGallerySection(sectionId);
      successToast("Section deleted successfully.");
    }
  };

  const handleDeleteImage = async (sectionId, imgId) => {
    if (
      await confirmDelete({
        title: "Delete Image?",
        text: "This image will be permanently deleted from the gallery.",
      })
    ) {
      await deleteImageFromBeautySection(sectionId, imgId);
      successToast("Image deleted successfully.");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-10">
      <h2 className="text-3xl font-bold text-pink-500 mb-6 text-center">Beauty Gallery Manager</h2>

      {/* Add Section */}
      <div className="bg-white p-6 rounded-2xl shadow-md border space-y-4">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">➕ Add New Section</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={newSectionName}
            onChange={(e) => setNewSectionName(e.target.value)}
            placeholder="Enter section name"
            className="border rounded-lg p-2 flex-1 focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
          <button
            onClick={handleAddSection}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition"
          >
            Add Section
          </button>
        </div>
      </div>

      {/* Upload Image */}
      <div className="bg-white p-6 rounded-2xl shadow-md border space-y-4">
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          🖼️ Add Image to Section
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedSectionId}
              onChange={(e) => setSelectedSectionId(e.target.value)}
              className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            >
              <option value="">Select section</option>
              {beautyGalleryData.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image Label
            </label>
            <input
              type="text"
              value={imageLabel}
              onChange={(e) => setImageLabel(e.target.value)}
              placeholder="Enter image label"
              className="w-full border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#D48C1E]"
            />
          </div>
        </div>
        <ImageUploadField
          label="Image File"
          hint="Recommended size: 800×600px"
          required
          file={selectedFile}
          onChange={(e) => setSelectedFile(e.target.files[0])}
          onClear={() => setSelectedFile(null)}
        />
        <div className="flex justify-end">
          <button
            onClick={handleAddImage}
            className="bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] px-6 py-2 rounded-lg transition font-medium"
            disabled={!selectedSectionId || !selectedFile}
          >
            Upload Image
          </button>
        </div>
      </div>

      {/* Display Gallery Sections */}
      <div className="space-y-8">
        {beautyGalleryData.map((section) => (
          <div key={section.id} className="bg-white p-6 rounded-2xl shadow-md border space-y-4">
            <div className="flex justify-between items-center">
              {editingSectionId === section.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingSectionName}
                    onChange={(e) => setEditingSectionName(e.target.value)}
                    className="border rounded-lg p-2"
                  />
                  <button
                    onClick={() => handleEditSectionName(section.id)}
                    className="bg-pink-500 text-white px-3 py-1 rounded hover:bg-pink-600"
                  >
                    Save
                  </button>
                </div>
              ) : (
                <>
                  <h4 className="text-2xl font-semibold text-gray-800">{section.name}</h4>
                  <button
                    onClick={() => {
                      setEditingSectionId(section.id);
                      setEditingSectionName(section.name);
                    }}
                    className="text-pink-500 text-sm hover:underline"
                  >
                    Edit
                  </button>
                </>
              )}
              <button
                onClick={() => handleDeleteSection(section.id)}
                className="bg-red-500 text-white text-sm px-3 py-1 rounded hover:bg-red-600 transition"
              >
                Delete Section
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {section.images &&
                section.images.map((img) => (
                  <div
                    key={img.id}
                    className="relative border rounded-xl overflow-hidden shadow hover:shadow-lg transition group"
                  >
                    <img
                      src={img.url}
                      alt={img.label}
                      className="w-full h-40 object-cover rounded-t-xl"
                    />
                    <div className="p-3 bg-gray-50 flex justify-between items-center">
                      {editingImageId === img.id ? (
                        <>
                          <input
                            type="text"
                            value={newLabel}
                            onChange={(e) => setNewLabel(e.target.value)}
                            className="border rounded p-1 text-sm flex-1 mr-2"
                          />
                          <button
                            onClick={() => handleUpdateLabel(section.id, img.id)}
                            className="bg-pink-500 text-white text-xs px-2 py-1 rounded hover:bg-pink-600"
                          >
                            Save
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-sm text-gray-700 truncate flex-1">{img.label}</span>
                          <button
                            onClick={() => startEditingLabel(img.id, img.label)}
                            className="text-pink-500 text-xs ml-2 hover:underline"
                          >
                            Edit
                          </button>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteImage(section.id, img.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full hover:bg-red-600 transition opacity-0 group-hover:opacity-100"
                    >
                      X
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BeautyGalleryManager;
